/**
 * SPA PDF a11y editor — entry point.
 *
 * Two-pane layout: Edit (card list) | PDF preview. The split fraction and
 * swap state are persisted to localStorage. An optional Inspector pane
 * (struct tree + edit panel) is toggled by Cmd+\.
 *
 * Design rationale: docs/ux/editor-ux-redesign-260501.md §3.
 * Decomposition history: docs/design/editor-ts-decompose-plan-260522.md
 *   — Phases EB.1–EB.7 extracted the eight clusters; Phase EC slimmed the
 *   orchestrator shell to its current shape (pdfjs bootstrap + pane-layout
 *   singleton + cross-cluster wiring + boot block + `selectStruct` —
 *   the categories that resisted decomp per §9.6).
 *
 * Toolbar / chrome features:
 *   (1) Empty-state hero preserved on direct nav.
 *   (2) Drop-target hidden once PDF loads.
 *   (3) "Open" button in toolbar (Ctrl+O).
 *   (4) "?" wired to focus-trapped hotkey-legend modal (Esc closes).
 *   (5) Default zoom is fit-to-width.
 *   (6) Loading overlay during parse + render with stage labels.
 *   (7) Zoom controls: toolbar +/-, clickable status-bar %, Ctrl+wheel.
 *   (8) Page navigation: ←/→ buttons, page-number input, keyboard hotkeys.
 *
 * What lives here (the categories that resisted decomp — design doc §9.6):
 *   - pdfjs bootstrap (the top-level `await import` + GlobalWorkerOptions);
 *   - `_paneLayout` singleton (orchestrator-owned per §9.1);
 *   - `loadFile` closure (binds `applyLayout` + `cardPaneDeps`);
 *   - `selectStruct` (touches `els.structList` — orchestrator-scope DOM);
 *   - Cross-cluster bindings (`setOverlayCallbackBindings`,
 *     `setMultipageCallbackBindings`, `installResizeReflow`,
 *     `installEmptyStateLoadFile`, `installKeyboardDeps`);
 *   - Boot block (storage-indicator idle paint → showEmptyState → URL
 *     params → IDB open → feedbackFab → initRecheck → autoOpenFromLocation
 *     OR session restore).
 */
import { log } from "./log.js";
import { autoOpenFromLocation } from "./url-autoopen.js";
import { updateOverlaySelection, loadOverlayPaletteFromServer, } from "./overlays.js";
import { applyOverlayDimensionMaskFromSearch } from "./overlay-dimension-mask.js";
import { loadOpenFile, openEditorDb } from "./session-store.js";
import { loadLocalOps, nextOpSeq, getOplogBuffer, installFlushHooks, } from "./idb-oplog.js";
import { createPaneLayout } from "./pane-layout.js";
import { mountFeedbackFab } from "./feedback.js";
import { ScreenReaderPanel, buildTranscript } from "./screen-reader.js";
// Phase FB.5 (frontend-mv-architecture-260524.md): PendingInsertStore relocated
// to store/ — the class IS state, not a flow controller. The remaining
// insert-flow.js exports (saveBlockState, resolvePendingType) are pure
// helpers + flow logic; those stay in the controllers-layer file.
import { PendingInsertStore } from "./store/pending-insert.js";
import { saveBlockState, resolvePendingType } from "./insert-flow.js";
import { buildPageFrames, MultiPageScrollManager, classifyPageZones, } from "./multi-page-scroll.js";
import { initRecheck } from "./recheck.js";
import { state, els, setEditorSelection, isEditorDirty, assertDocStateCleared, } from "./editor-state.js";
import { showEmptyState, installEmptyStateLoadFile, resetEditorState, } from "./editor-empty-state.js";
import { EditorLifecycleState } from "./editor-lifecycle-state.js";
import { announce } from "../a11y-view/announce.js";
import { setOverlayCallbackBindings, syncMcidToggleUI, installResizeReflow, } from "./editor-render.js";
import { setMultipageCallbackBindings, } from "./editor-multipage.js";
import { loadFile as _loadFileImpl, showLoading, setLoadingLabel, showLoadingError, } from "./editor-load-flow.js";
import { installKeyboardDeps, } from "./editor-modals-save.js";
import { showEditorToast } from "./editor-card-pane.js";
import { triggerBlobDownload } from "./download-util.js";
import { installGroundTruthExport, sha256Hex, applyExportButtonEnvGate, } from "./ground-truth-export-flow.js";
import { installCritiqueExport, isReviewerModeEnabled, generateUlid, } from "./critique-export-flow.js";
// Re-export onto the editor module namespace so console + integration tests
// can reach them without re-importing. Production wiring uses the imported
// names directly.
export const __editorPR2__ = {
    ScreenReaderPanel, buildTranscript,
    PendingInsertStore, saveBlockState, resolvePendingType,
    buildPageFrames, MultiPageScrollManager, classifyPageZones,
};
const PDFJS_BASE = "/static/editor/vendor/pdfjs";
const pdfjsLib = await import(`${PDFJS_BASE}/pdf.min.mjs`);
pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.mjs`;
// Hand the bootstrapped pdf.js bundle to the editor-load-flow module via
// the window-scoped seam. EB.6 extracted loadFile() but kept the top-level
// `await import(...)` here so the orchestrator stays the sole site that
// initialises pdfjs.GlobalWorkerOptions; load-flow only consumes the
// configured bundle.
window.__editorPdfjs = pdfjsLib;
// Local shorthand for optional DOM lookups (returns `HTMLElement | null`).
// Required elements go through `getRequired` / `querySelectorRequired` from
// dom-utils instead — they throw on missing rather than handing back null.
const $ = (id) => document.getElementById(id);
// ─── Two-pane layout (Edit | PDF Preview) ──────────────────────────────────
//
// Wired through `pane-layout.js`. The controller persists the split fraction
// (0–1) and swap state to localStorage; the Inspector pane is a hidden 3rd
// pane toggled by Cmd+\.
const _paneLayout = createPaneLayout({
    workspace: els.workspace,
    paneEditor: els.paneEditor,
    panePreview: els.panePreview,
    paneInspector: els.paneInspector,
    splitterEls: document.querySelectorAll(".pane-splitter"),
    swapBtn: $("editor-swap-panes-btn"),
    inspectorCloseBtns: document.querySelectorAll(".pane-collapse[data-pane-target=\"inspector\"]"),
});
const applyLayout = () => _paneLayout.applyLayout();
const toggleInspector = () => _paneLayout.toggleInspector();
// Apply initial layout on boot.
applyLayout();
// ─── `selectStruct` — orchestrator-owned (design doc §9.1 + §9.6) ─────────
//
// Touches `els.structList` (an orchestrator-scope DOM element — same
// sibling-pattern that kept `_paneLayout` here). Threaded into card-pane,
// multipage, and overlay binding setters via {selectStruct} bundles.
function selectStruct(structId, structListEl = els.structList) {
    setEditorSelection(structId ?? null);
    for (const row of structListEl.querySelectorAll(".editor-struct-row")) {
        row.classList.toggle("is-selected", row.dataset.structId === structId);
    }
    updateOverlaySelection(els.overlay, structId ?? null);
}
// ─── `loadFile` orchestrator wrapper ──────────────────────────────────────
//
// Wraps the editor-load-flow `loadFile` to bind the orchestrator-owned
// `applyLayout` callback and the post-EB.5 `cardPaneDeps` so call sites
// (event listeners, auto-open dep bag, session restore) keep the same
// `(file, opts?) => Promise<void>` shape. pane-layout remains an
// orchestrator-owned singleton — load-flow doesn't import it directly.
const loadFile = async (file, opts = {}) => _loadFileImpl(file, opts, applyLayout, { selectStruct, syncMcidToggleUI });
// ─── Reviewer-mode critique capture (J5b — opt-in, OFF by default) ──────────
//
// Critique capture is a corpus-authoring tool for REVIEWERS, gated behind BOTH a
// non-prod env AND an explicit opt-in signal (query `?reviewer=1` or localStorage
// `ada-critique-reviewer=1`). The opt-in is read synchronously here so a regular
// user (opt-in absent) captures NO baseline and gets NO critique button — their GT
// export stays byte-identical to today. The non-prod env half is confirmed later,
// after the /api/auth/me fetch (same signal as the GT export gate).
const _critiqueReviewerOptIn = (() => {
    try {
        if (new URLSearchParams(window.location.search).get("reviewer") === "1")
            return true;
        return window.localStorage.getItem("ada-critique-reviewer") === "1";
    }
    catch {
        // localStorage/URL unavailable (privacy mode / partial DOM) → not a reviewer.
        return false;
    }
})();
// The pre-edit baseline IR (the recovered structure the judge saw), deep-cloned at
// load ONLY in reviewer opt-in mode (behavior-neutral for regular users).
let _critiqueBaselineIr = null;
// ─── `loadDocument` — the atomic document-switch entry (single Open seam) ────
//
// THE single entry for opening ANY document. Every Open entry point (Open
// button / file picker, drag-drop, ?url= auto-open, session restore) routes
// through `loadDocument`, NEVER `loadFile` directly. When a document is already
// loaded (or loading), it atomically tears down ALL doc-derived state via
// `resetEditorState()` BEFORE `loadFile` mints a fresh transient job + interop
// for the new bytes — closing BUG #5 (a 2nd Open re-rendered the FIRST doc's
// structure because loadFile's init-if-empty guards kept the stale jobId).
//
// The reset→load is a tear-free atomic step: `resetEditorState()` is synchronous
// (its IDB writes are fire-and-forget) and no `await` sits between it and
// `loadFile`'s transition to LOADING, so no interleaving Open can observe a
// half-reset state (single-threaded JS event-loop semantics).
//
// Homed here in the composition root (not a leaf module, not a new module) per
// Epic editor-open-load-state-coherence-260813 Phase-1b R2/§G-Q5: editor.ts
// already imports both `loadFile` and `resetEditorState` cycle-free, and homing
// it here minimizes the collision surface with the ground-truth-authoring Epic
// (which refactors resetEditorState's BODY — untouched here).
const loadDocument = async (file, opts = {}) => {
    if (state.lifecycle.isAny([
        EditorLifecycleState.LOADED_TIER1,
        EditorLifecycleState.LOADED_FULL,
        EditorLifecycleState.LOADING,
    ])) {
        // §G-Q1 dirty-guard: the reset is IRREVERSIBLE (clears the undo stack,
        // pending inserts, and the persisted IDB file). Open-during-edit is a
        // likely mis-click, so confirm before discarding unsaved work. isEditorDirty
        // is the single queryable seam (reused by the future §G-Q2 same-doc guard).
        if (isEditorDirty() && !window.confirm(`Discard unsaved changes and open "${file.name}"?`)) {
            return;
        }
        const prevJobId = state.jobId;
        resetEditorState(); // synchronous, atomic → EMPTY_WARM (generation++)
        // §G-Q6(c): fail loud NOW if the reset missed a doc-derived field, rather
        // than silently rendering prior-document data on the load below.
        assertDocStateCleared();
        // rule #53: makes a future "stale structure" report pinnable to whether
        // a reset fired on the switch.
        log.info(`[editor] loadDocument: switch ${prevJobId ?? "(none)"} → (new document ${file.name})`);
    }
    await loadFile(file, opts);
    // J5b baseline snapshot: capture the freshly-loaded (pre-edit) IR as the
    // `recovered` structure for a later critique export. Reviewer-opt-in only —
    // a regular user captures nothing, so their export path is unchanged. Deep
    // clone via JSON round-trip (the IR is JSON wire data — the same shape
    // serializeGroundTruth consumes) so later edits to `state.ir` don't mutate it.
    if (_critiqueReviewerOptIn && state.ir) {
        try {
            _critiqueBaselineIr = JSON.parse(JSON.stringify(state.ir));
        }
        catch (err) {
            _critiqueBaselineIr = null;
            log.warn("[editor] critique baseline snapshot failed:", err);
        }
    }
};
// ─── Cross-cluster wiring ──────────────────────────────────────────────────
//
// The five `install*` / `set*` calls inject orchestrator-owned closures into
// the extracted modules. Each is a single-line install — the extracted
// module owns its event listeners + state; the orchestrator only supplies
// the closures that touch orchestrator-scope dependencies (`_paneLayout`,
// `els.structList`, `applyLayout`, `loadFile`).
setOverlayCallbackBindings({ selectStruct });
setMultipageCallbackBindings({ selectStruct });
// Route the picker + drag-drop Open paths through loadDocument (the atomic
// document-switch seam), NOT loadFile directly — so a 2nd Open resets first.
installEmptyStateLoadFile(loadDocument);
installResizeReflow(applyLayout);
installKeyboardDeps(toggleInspector);
// "Export as ground truth" toolbar action (ground-truth-authoring Epic Phase 3).
// The composition root injects the state/DOM seams so the flow module itself
// stays DOM-free (unit-testable). Sibling of Download — same browser-download
// plumbing (triggerBlobDownload), payload = the GroundTruthDoc JSON oracle.
const _exportGtButton = $("editor-export-gt-btn");
installGroundTruthExport({
    button: _exportGtButton,
    getIr: () => state.ir,
    // Per-page dims from the RENDERED page canvas (natural PDF-point dims,
    // set on `dataset.pageNaturalWidth/Height` at render — the same space as
    // Block.bbox). Load-bearing: the backend IR load path leaves `ir.pages`
    // unset, so this is the only reliable source for export normalization
    // (bug #1, 260813). Returns null for not-yet-rendered pages → the export
    // flow falls back to `ir.pages` (`composePageCanvas`).
    pageCanvas: (page) => {
        const desc = state.pageDescriptors[page - 1];
        if (!desc?.canvasEl)
            return null;
        const w = parseFloat(desc.canvasEl.dataset.pageNaturalWidth ?? "");
        const h = parseFloat(desc.canvasEl.dataset.pageNaturalHeight ?? "");
        return (w > 0 && h > 0) ? { w, h } : null;
    },
    getSourceName: () => state.fileName,
    getBuildVersion: () => document.querySelector('meta[name="ada-build-version"]')?.getAttribute("content") || "unknown",
    computeSha: async () => {
        if (!state.interop)
            return "";
        const bytes = await state.interop.serializeToBytes();
        return sha256Hex(new Uint8Array(bytes));
    },
    download: (blob, filename) => triggerBlobDownload(blob, filename),
    announce,
    onError: (message) => showEditorToast(message, "error"),
});
// Non-prod visibility gate: the export button is a corpus-authoring tool for US —
// shown in staging/local dev, REMOVED in prod (noise for customers). Reuse the
// existing /api/auth/me env signal (the same one env-banner.js consumes). Prod-safe
// default: any non-non-prod label OR a fetch failure removes the button, so prod
// never renders it. (A brief pre-resolve appearance is possible only if a document
// loads before this fetch resolves; the button is inert until clicked.)
// J5b reviewer-only "Export as critique" wiring. NO new served-HTML element: the
// button is created dynamically ONLY when reviewer mode is confirmed (non-prod env
// AND the opt-in signal), so a regular user's DOM + GT export path are unchanged
// (byte-identical). The critique flow REUSES serializeGroundTruth for both payloads
// (INV-CRIT-REUSE-EXPORT) and shares the GT deps' pageCanvas/computeSha seams.
const _installReviewerCritique = (envLabel) => {
    if (!isReviewerModeEnabled(envLabel, _critiqueReviewerOptIn))
        return;
    const critiqueBtn = document.createElement("button");
    critiqueBtn.type = "button";
    critiqueBtn.id = "editor-export-critique-btn";
    critiqueBtn.className = _exportGtButton?.className ?? "";
    critiqueBtn.textContent = "Export as critique";
    critiqueBtn.setAttribute("aria-label", "Export the correction as a judge critique (reviewer mode)");
    if (_exportGtButton?.parentElement) {
        _exportGtButton.parentElement.insertBefore(critiqueBtn, _exportGtButton.nextSibling);
    }
    else {
        document.body.appendChild(critiqueBtn);
    }
    installCritiqueExport({
        button: critiqueBtn,
        getIr: () => state.ir,
        getBaselineIr: () => _critiqueBaselineIr,
        pageCanvas: (page) => {
            const desc = state.pageDescriptors[page - 1];
            if (!desc?.canvasEl)
                return null;
            const w = parseFloat(desc.canvasEl.dataset.pageNaturalWidth ?? "");
            const h = parseFloat(desc.canvasEl.dataset.pageNaturalHeight ?? "");
            return (w > 0 && h > 0) ? { w, h } : null;
        },
        getSourceName: () => state.fileName,
        getBuildVersion: () => document.querySelector('meta[name="ada-build-version"]')?.getAttribute("content") || "unknown",
        computeSha: async () => {
            if (!state.interop)
                return "";
            const bytes = await state.interop.serializeToBytes();
            return sha256Hex(new Uint8Array(bytes));
        },
        newCritiqueId: () => generateUlid(),
        getSourceJobId: () => state.jobId ?? null,
        download: (blob, filename) => triggerBlobDownload(blob, filename),
        announce,
        onError: (message) => showEditorToast(message, "error"),
    });
};
void fetch("/api/auth/me")
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => {
    applyExportButtonEnvGate(_exportGtButton, data?.env_label);
    _installReviewerCritique(data?.env_label);
})
    .catch(() => {
    applyExportButtonEnvGate(_exportGtButton, null);
    // Fetch failed → prod-safe default: no reviewer critique button.
});
// ─── Boot ─────────────────────────────────────────────────────────────────
// Storage indicator: set the initial idle state. Runs unconditionally at
// boot (the indicator is a chrome-level UI element — present in both the
// empty-state hero and the loaded workspace). Lives in the boot block
// because it's a one-shot init with no per-PDF lifecycle.
els.storageDot.className = "editor-storage-dot";
els.storageLabel.textContent = "Storage idle";
showEmptyState();
// Boot diagnostic, gated behind localStorage.debug so it doesn't pollute
// the console for normal users.
try {
    if (window.localStorage?.getItem("debug") === "1") {
        log.info("[editor] booted; PDF.js =", pdfjsLib.version ?? "(unknown)");
    }
}
catch { /* localStorage unavailable in private mode */ }
// Capture the jobId from the URL once at boot so loadFile() can fetch
// /api/editor-ir/<jobId> in parallel with the WASM parse. Both `?job=`
// and `?jobId=` are accepted (the SPA's "Review & edit" link uses
// jobId).
{
    const _bootParams = new URLSearchParams(window.location.search);
    state.jobId = (_bootParams.get("jobId") ?? _bootParams.get("job")) ?? null;
}
// F7 Stage 5: open the v2 IDB once at boot. Non-fatal on failure — if the
// browser blocks IDB (private mode, full quota, schema-blocked by another
// tab), we run without persistence rather than refusing to start. Every
// call site that uses state.editorDb gates on truthiness.
openEditorDb()
    .then(async (db) => {
    state.editorDb = db;
    // Prime _idbLastOpSeq from any prior records for this session.
    // Memo §5.2: a new sessionId is minted on every open, so existing
    // records under this session are unusual (would only happen if the
    // ULID happened to collide with a prior open). Belt-and-braces:
    // resume from the highest matching opSeq if one exists.
    if (state.jobId) {
        try {
            const local = await loadLocalOps(db, state.jobId);
            state._idbLastOpSeq = nextOpSeq(local, state.currentSessionId) - 1;
        }
        catch (err) {
            log.warn("[editor] IDB loadLocalOps at boot failed:", err);
        }
    }
})
    .catch((err) => {
    log.warn("[editor] IDB open failed; running without persistence:", err);
});
// Mount the bottom-right Feedback FAB once at boot so it's available
// before a PDF is even loaded. The FAB POSTs to /api/feedback.
state.feedbackFab = mountFeedbackFab({
    getJobId: () => state.jobId,
    getCurrentFile: () => null, // file payload not yet wired
    onToast: (m) => announce(m),
});
// Path A Re-check: reveal the inspector-pane Re-check button at boot.
// Non-blocking — the section is revealed by initRecheck() unconditionally.
//
// `getPdfBytes` semantics: on the BACKEND path (jobId present),
// `serializeToBytes()` GETs the current SAVE checkpoint via
// `/api/editor-download/<jobId>` and does NOT close the document — the re-load
// below is a harmless no-op. On the WASM fallback path, `serializeToBytes()`
// closes the WASM document, so we re-load the freshly-serialized bytes to keep
// the editor usable for further edits. Either way the reload is invisible to the
// user — the PDF bytes round-trip through the engine unchanged. Retrofit design
// §2.5 (docs/design/pdf-editor-backend-session-retrofit-260715.md).
initRecheck({
    getPdfBytes: async () => {
        if (!state.interop)
            throw new Error("Editor is not loaded");
        const bytes = await state.interop.serializeToBytes();
        // WASM path: serializeToBytes() flipped _loaded=false — re-load to keep
        // the editor usable. Backend path: no-op re-flip. Same fileName so the
        // sidebar metadata is preserved.
        await state.interop.loadPdf(bytes, state.fileName ?? "edited.pdf");
        return bytes;
    },
}).catch((err) => {
    log.warn("[editor] Re-check init failed:", err);
});
// Signal that editor.js has fully evaluated and all synchronous event listeners
// are wired. Tests use window.__editorReady instead of waitForLoadState("networkidle")
// to detect when the module is ready — networkidle is an imprecise proxy that
// can add ~400ms of unnecessary wait per test. Reading a flag is instantaneous.
// See P3 of docs/design/editor-smoke-per-test-profile-260505.md.
window.__editorReady = true;
// Drain in-flight IDB writes; build-info.js reloadAfterCommit()
// awaits this before location.reload() so the user's tail edits land in IDB
// (and thus survive the reload-to-update flow). See
// docs/web/service-worker-cache-260506.md §"Reload helper" and Phase FB.10
// of docs/design/frontend-mv-architecture-260524.md §P.6.
//
// FB.10 rewires this against OplogBuffer.flush(): the per-op promise Set
// that pre-FB.10 tracked is retired; flush() is the single durable-on-disk
// settle point.
window.__edaFlushPendingIdbWrites = async () => {
    if (!state.editorDb)
        return;
    await getOplogBuffer(state.editorDb).flush();
};
// FB.10 flush triggers (d) + (e): beforeunload / pagehide / visibilitychange.
// Trigger (f) — Save button — is wired in editor-modals-save.ts (DOM-scoped).
// Trigger (c) — __edaFlushPendingIdbWrites — is wired above.
// Triggers (a) + (b) — size N + age T — are wired inside OplogBuffer itself.
installFlushHooks({
    addEventListener: (event, handler, opts) => window.addEventListener(event, handler, opts),
    removeEventListener: (event, handler) => window.removeEventListener(event, handler),
    document: {
        addEventListener: (event, handler) => document.addEventListener(event, handler),
        removeEventListener: (event, handler) => document.removeEventListener(event, handler),
        get visibilityState() { return document.visibilityState; },
    },
}, () => (state.editorDb ? getOplogBuffer(state.editorDb) : null));
// Fetch the canonical overlay-palette manifest and apply it. Non-blocking —
// the bootstrap defaults in overlays.js already mirror the manifest, so the
// first overlay render is correct even before this fetch resolves. Once the
// manifest lands the next render call sees the updated colours
// (applyOverlayPaletteManifest mutates the live palette in-place). Per
// docs/ux/editor-as-canonical-drawing-260505.md (Approach C).
loadOverlayPaletteFromServer().catch((err) => {
    log.warn("[editor] overlay palette manifest load failed:", err);
});
// Per-GateDimension overlay MASK (Epic a11y-vlm-staging-gate-261009 Phase 4):
// an additive `?overlayDim=<DIM>` boot flag renders the page with ONLY that
// dimension's construct overlay visible (the §5 masked-pass discipline in the
// visual channel) — the producer seam the Phase-5 VLM judge consumes. Absent
// the param this is a no-op and the full-overlay view is unchanged. Class-based
// `<style>` injection, so it also masks overlays re-rendered on later
// zoom/edit without re-running.
const _overlayDim = applyOverlayDimensionMaskFromSearch(document, window.location.search);
if (_overlayDim) {
    log.info("[editor] overlay dimension mask active:", _overlayDim);
}
// URL deep-link auto-open takes precedence over session restore: an explicit
// ?job= / ?url= param is the user expressing fresh intent (e.g., a shared
// link), and should override any previously-restored file. If neither param
// is present, fall through to session restore so a back-button navigation
// from /editor → / → back-button reconstitutes the prior open file.
const _urlParams = new URLSearchParams(window.location.search);
// `jobId` is an alias of `job` (the SPA's "Review & edit" link uses ?jobId=).
// Both must gate the auto-open path; otherwise ?jobId= silently falls through
// to session restore and the user sees an empty editor. `file` is the
// dev-only local-file path; same gating treatment.
const _hasAutoOpenParam = _urlParams.has("job") || _urlParams.has("jobId") || _urlParams.has("url") ||
    _urlParams.has("file") || _urlParams.has("filename");
if (_hasAutoOpenParam) {
    /* eslint-disable promise/prefer-await-to-then -- fire-and-forget at module top-level; cannot await outside an async function at module scope */
    autoOpenFromLocation({
        location: window.location,
        history: window.history,
        fetch: window.fetch.bind(window),
        confirm: window.confirm.bind(window),
        // The ?url= / ?job= / ?file= auto-open sink is the atomic document-switch
        // seam (loadDocument), not loadFile directly — an explicit deep-link is
        // fresh intent that must reset any restored/prior document first.
        loadFile: loadDocument,
        showLoading,
        setLoadingLabel,
        showLoadingError,
    }).catch((err) => {
        log.error("[editor] auto-open failed:", err);
    });
    /* eslint-enable promise/prefer-await-to-then */
}
else {
    // Restore any previously-loaded file from session storage. The bytes
    // live in IndexedDB, metadata in sessionStorage.
    void (async () => {
        try {
            const restored = await loadOpenFile();
            if (restored) {
                // Session restore routes through loadDocument too — uniform Open
                // seam (→ A.19). On boot the lifecycle is EMPTY_COLD so no reset
                // fires; this just keeps every Open entry point on one door.
                await loadDocument(restored, { skipPersist: true });
            }
        }
        catch (err) {
            log.warn("[editor] file restore from session failed:", err);
        }
    })();
}
